import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, MapPin, Cloud, Users, FileText, Clock, Search, Trash2, Edit, Eye, Camera, X, ArrowLeft, AlertTriangle, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const FieldLogs = ({ user }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingLog, setViewingLog] = useState(null);
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);

  // Filters
  const [filterLocation, setFilterLocation] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Form
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    location: '',
    weather: '',
    crewSize: '',
    workPerformed: '',
    delays: '',
    visitors: '',
    notes: '',
    safetyObservations: '',
  });

  useEffect(() => {
    loadLogs();
    loadLocations();
  }, [filterLocation, filterDateFrom, filterDateTo]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterLocation) params.append('location', filterLocation);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const { data } = await axiosInstance.get(`/field-logs?${params}`);
      setLogs(data);
    } catch (err) {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const { data } = await axiosInstance.get('/field-logs/locations');
      setLocations(data);
    } catch {
      // ignore
    }
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      location: '',
      weather: '',
      crewSize: '',
      workPerformed: '',
      delays: '',
      visitors: '',
      notes: '',
      safetyObservations: '',
    });
    setEditingLog(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleEdit = (log) => {
    setForm({
      date: log.date || '',
      location: log.location || '',
      weather: log.weather || '',
      crewSize: log.crewSize || '',
      workPerformed: log.workPerformed || '',
      delays: log.delays || '',
      visitors: log.visitors || '',
      notes: log.notes || '',
      safetyObservations: log.safetyObservations || '',
    });
    setEditingLog(log);
    setShowCreateDialog(true);
  };

  const handleView = (log) => {
    setViewingLog(log);
    setShowViewDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.location || !form.workPerformed || !form.date) {
      toast.error(t('fieldLogs.requiredFields'));
      return;
    }
    try {
      const payload = { ...form, crewSize: form.crewSize ? parseInt(form.crewSize) : null };
      if (editingLog) {
        await axiosInstance.put(`/field-logs/${editingLog.id}`, payload);
        toast.success(t('fieldLogs.logUpdated'));
      } else {
        await axiosInstance.post('/field-logs', payload);
        toast.success(t('fieldLogs.logCreated'));
      }
      setShowCreateDialog(false);
      resetForm();
      loadLogs();
      loadLocations();
    } catch (err) {
      toast.error(t('common.saveFailed'));
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await axiosInstance.delete(`/field-logs/${logId}`);
      toast.success(t('common.deleteSuccess'));
      loadLogs();
    } catch (err) {
      toast.error(t('common.deleteFailed'));
    }
  };

  const handlePhotoUpload = async (logId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axiosInstance.post(`/field-logs/${logId}/photos`, formData);
      toast.success(t('fieldLogs.photoUploaded'));
      loadLogs();
      // Refresh viewing log
      const { data } = await axiosInstance.get(`/field-logs/${logId}`);
      setViewingLog(data);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handlePhotoDelete = async (logId, photoId) => {
    try {
      await axiosInstance.delete(`/field-logs/${logId}/photos/${photoId}`);
      toast.success(t('common.deleteSuccess'));
      const { data } = await axiosInstance.get(`/field-logs/${logId}`);
      setViewingLog(data);
      loadLogs();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDownloadPdf = async (logId) => {
    try {
      const response = await axiosInstance.get(`/field-logs/${logId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `field_log_${logId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('common.pdfDownloaded'));
    } catch {
      toast.error(t('common.pdfFailed'));
    }
  };

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterLocation) params.append('location', filterLocation);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const response = await axiosInstance.get(`/field-logs/export/csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'field_logs_export.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('fieldLogs.csvExported'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE);
  const paginatedLogs = logs.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'company_admin' || user?.role === 'division_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Button variant="ghost" onClick={() => window.history.back()} className="mb-2" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="field-logs-title">
            {t('fieldLogs.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('fieldLogs.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            {t('fieldLogs.exportCsv')}
          </Button>
          <Button onClick={handleCreate} data-testid="create-field-log-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('fieldLogs.newEntry')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Search className="w-4 h-4" />
            {t('fieldLogs.filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>{t('common.location')}</Label>
              <Select value={filterLocation || "all"} onValueChange={(v) => { setFilterLocation(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="filter-location">
                  <SelectValue placeholder={t('fieldLogs.allLocations')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('fieldLogs.allLocations')}</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('fieldLogs.dateFrom')}</Label>
              <Input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
            </div>
            <div>
              <Label>{t('fieldLogs.dateTo')}</Label>
              <Input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-600">{t('fieldLogs.noLogs')}</p>
            <p className="text-sm text-gray-500 mt-1">{t('fieldLogs.noLogsDesc')}</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              {t('fieldLogs.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedLogs.map(log => (
              <Card key={log.id} className="hover:shadow-md transition-shadow" data-testid={`field-log-${log.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {log.date}
                        </Badge>
                        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {log.location}
                        </Badge>
                        {log.weather && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Cloud className="w-3 h-3" />
                            {log.weather}
                          </Badge>
                        )}
                        {log.crewSize && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {log.crewSize}
                          </Badge>
                        )}
                        {log.photos?.length > 0 && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Camera className="w-3 h-3" />
                            {log.photos.length}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 line-clamp-2">{log.workPerformed}</p>
                      {isAdmin && log.createdByName && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('common.by')} {log.createdByName} ({log.createdByRole})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleView(log)} data-testid={`view-log-${log.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        {t('common.view')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(log.id)} data-testid={`download-log-${log.id}`}>
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      {(log.createdBy === user?.userId || log.createdBy === user?.id || isAdmin) && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleEdit(log)} data-testid={`edit-log-${log.id}`}>
                            <Edit className="w-4 h-4 mr-1" />
                            {t('common.edit')}
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(log.id)} data-testid={`delete-log-${log.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('common.previous')}
              </Button>
              <span className="text-sm text-gray-600">
                {t('common.step')} {page} {t('common.of')} {totalPages}
              </span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                {t('common.next')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="field-log-dialog-title">
              {editingLog ? t('fieldLogs.editEntry') : t('fieldLogs.newEntry')}
            </DialogTitle>
            <DialogDescription>
              {editingLog ? t('fieldLogs.editEntryDesc') : t('fieldLogs.newEntryDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.date')} *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-log-date" />
              </div>
              <div>
                <Label>{t('common.location')} *</Label>
                <Input placeholder={t('fieldLogs.locationPlaceholder')} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="field-log-location" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('fieldLogs.weather')}</Label>
                <Input placeholder={t('fieldLogs.weatherPlaceholder')} value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} data-testid="field-log-weather" />
              </div>
              <div>
                <Label>{t('fieldLogs.crewSize')}</Label>
                <Input type="number" placeholder={t('fieldLogs.crewSizePlaceholder')} value={form.crewSize} onChange={(e) => setForm({ ...form, crewSize: e.target.value })} data-testid="field-log-crew" />
              </div>
            </div>

            <div>
              <Label>{t('fieldLogs.workPerformed')} *</Label>
              <Textarea placeholder={t('fieldLogs.workPerformedPlaceholder')} value={form.workPerformed} onChange={(e) => setForm({ ...form, workPerformed: e.target.value })} rows={3} data-testid="field-log-work" />
            </div>

            <div>
              <Label>{t('fieldLogs.delays')}</Label>
              <Textarea placeholder={t('fieldLogs.delaysPlaceholder')} value={form.delays} onChange={(e) => setForm({ ...form, delays: e.target.value })} rows={2} data-testid="field-log-delays" />
            </div>

            <div>
              <Label>{t('fieldLogs.visitors')}</Label>
              <Input placeholder={t('fieldLogs.visitorsPlaceholder')} value={form.visitors} onChange={(e) => setForm({ ...form, visitors: e.target.value })} data-testid="field-log-visitors" />
            </div>

            <div>
              <Label>{t('fieldLogs.safetyObservations')}</Label>
              <Textarea placeholder={t('fieldLogs.safetyObservationsPlaceholder')} value={form.safetyObservations} onChange={(e) => setForm({ ...form, safetyObservations: e.target.value })} rows={2} data-testid="field-log-safety" />
            </div>

            <div>
              <Label>{t('fieldLogs.generalNotes')}</Label>
              <Textarea placeholder={t('fieldLogs.generalNotesPlaceholder')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="field-log-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} data-testid="field-log-submit">
              {editingLog ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingLog && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="view-log-title">{t('fieldLogs.viewEntry')}</DialogTitle>
                <DialogDescription>
                  {viewingLog.date} — {viewingLog.location}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{t('common.date')}:</span> {viewingLog.date}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{t('common.location')}:</span> {viewingLog.location}
                  </div>
                  {viewingLog.weather && (
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{t('fieldLogs.weather')}:</span> {viewingLog.weather}
                    </div>
                  )}
                  {viewingLog.crewSize && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{t('fieldLogs.crewSize')}:</span> {viewingLog.crewSize}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.workPerformed')}</h4>
                  <p className="text-sm bg-gray-50 p-3 rounded">{viewingLog.workPerformed}</p>
                </div>

                {viewingLog.delays && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.delays')}</h4>
                    <p className="text-sm bg-orange-50 p-3 rounded text-orange-800">{viewingLog.delays}</p>
                  </div>
                )}

                {viewingLog.visitors && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.visitors')}</h4>
                    <p className="text-sm bg-blue-50 p-3 rounded">{viewingLog.visitors}</p>
                  </div>
                )}

                {viewingLog.safetyObservations && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.safetyObservations')}</h4>
                    <p className="text-sm bg-green-50 p-3 rounded text-green-800">{viewingLog.safetyObservations}</p>
                  </div>
                )}

                {viewingLog.notes && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.generalNotes')}</h4>
                    <p className="text-sm bg-gray-50 p-3 rounded">{viewingLog.notes}</p>
                  </div>
                )}

                {/* Photos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-gray-700">{t('fieldLogs.photos')}</h4>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) handlePhotoUpload(viewingLog.id, e.target.files[0]);
                        }}
                      />
                      <span className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                        <Camera className="w-4 h-4" />
                        {t('fieldLogs.addPhoto')}
                      </span>
                    </label>
                  </div>
                  {viewingLog.photos?.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {viewingLog.photos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={`/api/photos/${photo.gridfsId}`}
                            alt={photo.filename}
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            onClick={() => handlePhotoDelete(viewingLog.id, photo.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('fieldLogs.noPhotos')}</p>
                  )}
                </div>

                {viewingLog.createdByName && (
                  <p className="text-xs text-gray-400 mt-2">
                    {t('common.by')} {viewingLog.createdByName} — {new Date(viewingLog.createdAt).toLocaleString()}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => handleDownloadPdf(viewingLog.id)}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('fieldLogs.downloadPdf')}
                </Button>
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldLogs;
